import { useEffect } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Badge,
  FormModal,
  Text,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { toApiDate } from "@marekl/adminer/datetime";
import { useUpdatePerson, type PersonPayload } from "../../../lib/queries/memberships";
import type { PersonListItem } from "../../../lib/queries/persons";
import {
  FULL_DATA_FIELDS,
  makePersonEditSchema,
  personEqual,
  personToFormValues,
  type MemberFormOutput,
  type MemberFormValues,
} from "../../team/TeamDetailPage/memberFormSchema";
import { MemberFormFields } from "../../team/TeamDetailPage/MemberFormFields";
import { PhotoUploadField } from "../../../components/PhotoUploadField";
import { memberBadge } from "../../../lib/memberState";
import { useSeason } from "../../../season";

function toValues(person: PersonListItem): MemberFormValues {
  return {
    person: personToFormValues(person),
    // Inert carrier fields that keep the one shared form shape — never
    // rendered, never submitted. Do not "clean them up": dropping them
    // would fork the form type again. membership_kind still drives the
    // schema's conditional requiredness (a member owes the full ČNSMS data
    // set), exactly like the watched value in the roster dialog; it is
    // fixed at open because the control is hidden here.
    roles: [],
    membership_kind: person.is_member ? "registered" : "guest",
  };
}

/**
 * EditPersonModal is the members registry's thin shell over the same
 * MemberFormFields + memberFormSchema the roster's EditMemberModal uses, so
 * the person fields cannot diverge between the two dialogs. It edits the
 * Person alone: roles and membership kind are per-team, per-season facts
 * edited on the soupiska, so both blocks are hidden here.
 *
 * No identityLock is passed: only admin / members_admin can reach this page
 * (ClenskaZakladnaRoute), and membership.UpdatePerson skips the identity
 * lock for exactly those roles.
 */
export function EditPersonModal({
  person,
  open,
  onClose,
}: {
  person: PersonListItem;
  open: boolean;
  onClose: () => void;
}) {
  const kind = person.is_member ? "registered" : ("guest" as const);
  // Rodné číslo a adresa se ukážou, když je ČNSMS o téhle osobě vede: buď je
  // letos členka, nebo tam nějaká hodnota už je.
  //
  // `kind` samo by nestačilo. Odvozuje se z `is_member`, a to platí jen pro
  // aktivní sezónu, takže loňská členka, která se letos ještě nepřeregistrovala,
  // čte jako „guest" a přišla by o vyplněné rodné číslo právě na obrazovce,
  // která ho má opravovat (makePersonEditSchema ta pole schválně nevyžaduje
  // přesně proto, aby šel neúplný záznam spravit). Mezi sezónami, než se kluby
  // zaregistrují, je takhle celá členská základna.
  //
  // A zároveň se neukážou tam, kde je ČNSMS nevede: u nečlenky bez údajů a
  // u zahraniční členky, po které se rodné číslo neeviduje vůbec. Doplnit je
  // někomu, kdo nemá nic, je věc soupisky: povýšení na člena nabídne pole
  // v EditMemberModal a vedoucí bez rodného čísla se dá opravit v tabulce
  // „Registrace do sezóny" (na soupisce být musí, ErrLeaderNotOnRoster).
  const showCmfData =
    person.is_member || FULL_DATA_FIELDS.some((f) => (person[f] ?? "").trim() !== "");
  // Membership is a per-season fact, so the badge below must name the
  // season it was computed against (same rule as the table column).
  const { activeSeason } = useSeason();

  const updatePerson = useUpdatePerson(person.id);

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<MemberFormValues, unknown, MemberFormOutput>({
    resolver: zodResolver(makePersonEditSchema(person)) as Resolver<
      MemberFormValues,
      unknown,
      MemberFormOutput
    >,
    defaultValues: toValues(person),
    mode: "onTouched",
  });

  useEffect(() => {
    if (open) reset(toValues(person));
  }, [open, person, reset]);

  async function onSubmit(values: MemberFormOutput) {
    const payload: PersonPayload = {
      ...values.person,
      birth_date: toApiDate(values.person.birth_date) ?? "",
    };
    // A no-op save closes without a request — an unconditional PATCH would
    // write an audit row saying nothing changed.
    if (personEqual(payload, person)) {
      onClose();
      return;
    }
    try {
      await updatePerson.mutateAsync(payload);
      onClose();
    } catch (err) {
      apiErrorToFormErrors<MemberFormValues>(err, setError, {
        fieldMap: (field) => `person.${field}` as never,
        fallbackMessage: "Změny se nepodařilo uložit.",
      });
    }
  }

  const rootMessage = rootErrorMessage(errors);

  const badge = memberBadge(kind);

  return (
    <FormModal
      open={open}
      title={`Upravit osobu: ${person.first_name} ${person.last_name}`}
      submitLabel="Uložit změny"
      busy={updatePerson.isPending}
      error={rootMessage}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      width="lg"
    >
      {/* Členství is not a person attribute and is not editable here:
          registering someone into a season happens on the team's soupiska,
          which is also where the required data is collected. */}
      <div>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <Text as="p" variant="muted">
          {activeSeason ? `Členství v sezóně ${activeSeason.code}. ` : ""}
          Registrace do sezóny se provádí na soupisce týmu.
        </Text>
      </div>
      <MemberFormFields
        register={register}
        control={control}
        errors={errors}
        hideKind
        hideRoles
        kind={kind}
        showCmfData={showCmfData}
      />
      {(person.is_member || person.photo_uploaded) && (
        // Photo upload is a standalone side action (its own mutation),
        // independent of the RHF submit above. teamId null → the global
        // POST /persons/:id/photo. A member owes a photo; a nečlen does
        // not (never on an MČR soupiska), but one who HAS a photo from a
        // past season must still be able to replace it — photos are
        // replace-only, and this modal is the only global surface.
        <PhotoUploadField
          teamId={null}
          personId={person.id}
          hasPhoto={person.photo_uploaded ?? false}
        />
      )}
    </FormModal>
  );
}
